/**
 * Insights doorway for Eligibility & Revenue pulse — material CMS diffs.
 */
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Activity } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useProgramYear } from '@/contexts/ProgramYearContext';
import { MipsDataService } from '@/lib/mips-data-service';
import { apiClient } from '@/lib/api-client';
import { keepPreviousSameEntity } from '@/lib/query-keys';
import { ReportCard } from '../ReportCard';

export function EligibilityChangesCard() {
  const navigate = useNavigate();
  const { programYear } = useProgramYear();
  const { selectedPractice } = useAuth();
  const available = MipsDataService.getInstance().hasMvpDataset(programYear);
  const practiceId = selectedPractice?.id;

  const pulseQ = useQuery<{ totalEvents?: number; summary?: { materialCount: number } }>({
    queryKey: ['mvp', 'eligibility-changes', practiceId ?? '', programYear, 'within_year'],
    queryFn: () =>
      apiClient.get(
        `/api/mvp/${practiceId}/eligibility-changes?year=${programYear}&mode=within_year`,
      ),
    enabled: !!practiceId && available,
    placeholderData: keepPreviousSameEntity(2, String(practiceId ?? '')),
    staleTime: 60_000,
  });

  const materialCount =
    pulseQ.data?.summary?.materialCount ?? pulseQ.data?.totalEvents ?? null;

  const preview = (
    <div className="text-center py-3">
      <p className="text-3xl font-bold tabular-nums">
        {!available
          ? '—'
          : materialCount == null && pulseQ.isPending
            ? '…'
            : materialCount ?? 0}
      </p>
      <p className="text-xs text-muted-foreground mt-1">
        {!available
          ? `available from PY2026 (viewing PY${programYear})`
          : materialCount === 0
            ? 'no material CMS changes this year'
            : 'material eligibility / status / revenue moves'}
      </p>
    </div>
  );

  return (
    <ReportCard
      title="Eligibility & Revenue"
      description="Material CMS eligibility, special-status, and revenue changes between refreshes. Identical hydrates stay silent."
      icon={<Activity className="h-6 w-6" />}
      onClick={() => navigate('/insights/eligibility-changes')}
      status={available ? 'available' : 'coming-soon'}
      preview={preview}
    />
  );
}
